import { useEffect, useState } from "react";
import { Trophy, FileDown, Archive, Trash2, Download } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { api, formatApiError, formatDate } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { openBlob } from "@/lib/docOptions";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

const MONTHS = ["Oca", "Şub", "Mar", "Nis", "May", "Haz", "Tem", "Ağu", "Eyl", "Eki", "Kas", "Ara"];
const COLORS = ["#059669", "#0284c7", "#d97706", "#7c3aed", "#dc2626", "#0f172a", "#65a30d"];

const Archives = () => {
  const [items, setItems] = useState([]);
  const load = () => api.get("/reports/archives").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const download = async (a) => { await openBlob(`/reports/archives/${a.id}/pdf`); setTimeout(load, 1500); };
  const remove = async (a) => { try { await api.delete(`/reports/archives/${a.id}`); toast.success("Raporlama alanından kaldırıldı"); load(); } catch (x) { toast.error(formatApiError(x.response?.data?.detail)); } };
  return (
    <Card className="p-5 space-y-3" data-testid="report-archives">
      <div className="font-semibold flex items-center gap-2"><Archive className="w-4 h-4" />Yıl sonu arşivleri</div>
      <p className="text-xs text-slate-500">Biten her yılın raporu otomatik arşivlenir. 2 yıldan eski arşivler indirildikten sonra raporlama alanından kaldırılabilir.</p>
      {items.length === 0 ? <div className="text-sm text-slate-500">Arşiv yok</div> : items.map((a) => (
        <div key={a.id} data-testid={`archive-row-${a.year}`} className="flex items-center justify-between rounded-lg border p-3 text-sm">
          <div><b>{a.year}</b> · {(a.size / 1024).toFixed(0)} KB · {a.downloaded_at ? `İndirildi ${formatDate(a.downloaded_at)}` : "Henüz indirilmedi"}</div>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" data-testid={`archive-download-${a.year}`} onClick={() => download(a)}><Download className="w-4 h-4 mr-1" />İndir</Button>
            {a.can_remove && <Button size="sm" variant="ghost" data-testid={`archive-remove-${a.year}`} onClick={() => remove(a)}><Trash2 className="w-4 h-4 text-red-600" /></Button>}
          </div>
        </div>
      ))}
    </Card>
  );
};

export default function ReportsPage() {
  const { user } = useAuth();
  const [year, setYear] = useState(new Date().getFullYear());
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/reports/performance", { params: { year } }).then((r) => setData(r.data)); }, [year]);
  if (!data) return <div className="text-slate-500">Yükleniyor...</div>;
  const keys = Object.keys(data.labels);
  const star = data.week_star?.winner;
  const chart = data.monthly.map((m) => ({ name: MONTHS[m.month - 1], ...Object.fromEntries(data.board.map((b) => [b.name, m[b.user_id] || 0])) }));

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "Outfit, sans-serif" }}>Performans Raporları</h1>
          <p className="text-slate-500 text-sm mt-1">{user.role === "CONSULTANT" ? "Kendi performansınız" : "Danışmanların işlem ve performans verileri"} · {year} takvim yılı</p>
        </div>
        <div className="flex gap-2">
          <Select value={String(year)} onValueChange={(v) => setYear(Number(v))}>
            <SelectTrigger className="w-28" data-testid="report-year"><SelectValue /></SelectTrigger>
            <SelectContent>{data.years.map((y) => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
          </Select>
          <Button data-testid="report-pdf-btn" className="bg-slate-900" onClick={() => openBlob(`/reports/performance/pdf?year=${year}`)}><FileDown className="w-4 h-4 mr-1" />PDF</Button>
        </div>
      </div>

      <Card className="p-5 flex items-center gap-4 bg-gradient-to-r from-amber-50 to-white border-amber-200" data-testid="week-star-card">
        <div className="w-12 h-12 rounded-full bg-amber-400 text-white flex items-center justify-center"><Trophy className="w-6 h-6" /></div>
        <div>
          <div className="text-xs uppercase tracking-wider text-amber-700 font-semibold">Haftanın danışmanı · {formatDate(data.week_star.week_start)} haftası</div>
          <div className="text-xl font-bold text-slate-900" data-testid="week-star-name">{star ? `${star.name} – ${star.score} puan` : "Bu hafta henüz puan alan danışman yok"}</div>
        </div>
      </Card>

      <Card className="overflow-x-auto">
        <table className="w-full text-sm" data-testid="report-table">
          <thead className="bg-slate-50 text-xs text-slate-500"><tr><th className="text-left p-3">#</th><th className="text-left p-3">Danışman</th>{keys.map((k) => <th key={k} className="p-3 text-center">{data.labels[k]}</th>)}<th className="p-3 text-center">Puan</th></tr></thead>
          <tbody>{data.board.map((b, i) => (
            <tr key={b.user_id} className="border-t" data-testid={`report-row-${b.user_id}`}>
              <td className="p-3 font-semibold">{i + 1}</td><td className="p-3 font-medium">{b.name}</td>
              {keys.map((k) => <td key={k} className="p-3 text-center">{b.metrics[k]}</td>)}
              <td className="p-3 text-center font-bold text-emerald-700" data-testid={`report-score-${b.user_id}`}>{b.score}</td>
            </tr>
          ))}</tbody>
        </table>
      </Card>

      {chart.length > 0 && (
        <Card className="p-5">
          <div className="font-semibold mb-3">Aylık puanlar</div>
          <div className="h-72"><ResponsiveContainer><BarChart data={chart}><XAxis dataKey="name" fontSize={12} /><YAxis fontSize={12} /><Tooltip /><Legend />
            {data.board.map((b, i) => <Bar key={b.user_id} dataKey={b.name} fill={COLORS[i % COLORS.length]} radius={[3, 3, 0, 0]} />)}</BarChart></ResponsiveContainer></div>
        </Card>
      )}

      <Card className="p-4 text-xs text-slate-600 bg-slate-50" data-testid="score-info">
        <b>Puanlar nasıl hesaplanır?</b> Her işlem, ilgili takvim yılı içinde yapıldığı tarihe göre sayılır: {keys.map((k) => `${data.labels[k]} = ${data.weights[k]} puan`).join(" · ")}. Haftanın danışmanı, Pazartesi–Pazar arasında en yüksek puanı alan danışmandır. Puan değerlerini Broker, Ayarlar &gt; Performans sekmesinden değiştirebilir.
      </Card>

      {["BROKER", "ACCOUNTANT"].includes(user.role) && <Archives />}
    </div>
  );
}
